import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

import { getUserProfile } from '../api';


const AuthContext = createContext();
const gymBackend = (process.env.REACT_APP_GYM_BACKEND_URL || 'http://127.0.0.1:8002').replace(/\/$/, '');
const TOKEN_STORAGE_KEY = 'authTokens';
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;
const REFRESH_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export default AuthContext;


const readStoredTokens = () => {
    if (typeof window === 'undefined') return null;

    try {
        const rawValue = window.localStorage.getItem(TOKEN_STORAGE_KEY);
        if (!rawValue) return null;

        const tokens = JSON.parse(rawValue);
        if (typeof tokens?.access !== 'string' || typeof tokens?.refresh !== 'string') {
            window.localStorage.removeItem(TOKEN_STORAGE_KEY);
            return null;
        }
        return tokens;
    } catch (error) {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        return null;
    }
};


const decodeAccessToken = (accessToken) => {
    try {
        return jwtDecode(accessToken);
    } catch (error) {
        return null;
    }
};


const readApiMessage = (data, fallback) => {
    if (typeof data?.detail === 'string') return data.detail;
    if (typeof data?.error === 'string') return data.error;
    if (typeof data?.message === 'string') return data.message;

    if (data && typeof data === 'object') {
        const firstValue = Object.values(data)[0];
        if (Array.isArray(firstValue) && firstValue.length) {
            return String(firstValue[0]);
        }
    }
    return fallback;
};


export const AuthProvider = ({ children }) => {
    const initialTokens = useMemo(() => readStoredTokens(), []);
    const [authTokens, setAuthTokens] = useState(initialTokens);
    const [user, setUser] = useState(() => (
        initialTokens ? decodeAccessToken(initialTokens.access) : null
    ));
    const [loading, setLoading] = useState(true);
    const [loginStatus, setLoginStatus] = useState({
        loading: false,
        success: false,
        message: '',
    });
    const navigate = useNavigate();

    const resetLoginStatus = useCallback(() => {
        setLoginStatus({ loading: false, success: false, message: '' });
    }, []);

    const persistTokens = useCallback((tokens) => {
        const decodedUser = decodeAccessToken(tokens?.access);
        if (!decodedUser || !tokens?.refresh) return false;

        window.localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
        setAuthTokens(tokens);
        setUser(decodedUser);
        return true;
    }, []);

    const clearSession = useCallback(() => {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        window.localStorage.removeItem('gymId');
        setAuthTokens(null);
        setUser(null);
    }, []);

    const logoutUser = useCallback(() => {
        clearSession();
        resetLoginStatus();
        setLoading(false);
    }, [clearSession, resetLoginStatus]);

    const fetchUserProfileAndStoreGymId = useCallback(async (accessToken) => {
        try {
            const profileData = await getUserProfile(accessToken);
            if (profileData?.id) {
                window.localStorage.setItem('gymId', profileData.id);
            }
        } catch (error) {
            // L'échec du profil ne doit pas déconnecter une session JWT valide.
            console.error('Impossible de récupérer le profil de la salle.', error);
        }
    }, []);

    const refreshAccessToken = useCallback(async (tokens, options = {}) => {
        if (!tokens?.refresh) {
            if (options.clearInvalid !== false) clearSession();
            return false;
        }

        try {
            const response = await fetch(`${gymBackend}/auth/token-refresh/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refresh: tokens.refresh }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                if ([400, 401, 403].includes(response.status)) clearSession();
                return false;
            }

            const refreshedTokens = {
                access: data.access,
                refresh: data.refresh || tokens.refresh,
            };
            return persistTokens(refreshedTokens);
        } catch (error) {
            // Une panne réseau temporaire ne supprime plus la session persistée.
            console.error('Le renouvellement de la session a échoué.', error);
            return false;
        }
    }, [clearSession, persistTokens]);

    const loginUserWithToken = useCallback(async ({ access, refresh }) => {
        setLoginStatus({ loading: true, success: false, message: '' });

        if (!persistTokens({ access, refresh })) {
            setLoginStatus({
                loading: false,
                success: false,
                message: 'La session reçue est invalide.',
            });
            return;
        }

        await fetchUserProfileAndStoreGymId(access);
        setLoginStatus({
            loading: false,
            success: true,
            message: 'Connexion réussie.',
        });
        navigate('/home', { replace: true });
    }, [fetchUserProfileAndStoreGymId, navigate, persistTokens]);

    const loginUser = useCallback(async (event) => {
        event.preventDefault();
        setLoginStatus({ loading: true, success: false, message: '' });

        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);

        try {
            const response = await fetch(`${gymBackend}/auth/login/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    identifier: event.target.username.value.trim(),
                    password: event.target.password.value,
                }),
            });
            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const fallback = response.status === 401
                    ? 'Adresse e-mail ou mot de passe incorrect.'
                    : response.status === 403
                        ? "Votre adresse e-mail n'est pas encore confirmée."
                        : 'La connexion a échoué. Veuillez réessayer.';
                setLoginStatus({
                    loading: false,
                    success: false,
                    message: readApiMessage(data, fallback),
                });
                return;
            }

            if (!persistTokens({ access: data.access, refresh: data.refresh })) {
                setLoginStatus({
                    loading: false,
                    success: false,
                    message: 'La session reçue est invalide.',
                });
                return;
            }

            await fetchUserProfileAndStoreGymId(data.access);
            setLoginStatus({
                loading: false,
                success: true,
                message: 'Connexion réussie.',
            });
            navigate('/home', { replace: true });
        } catch (error) {
            const message = error.name === 'AbortError'
                ? 'Le serveur met trop de temps à répondre. Veuillez réessayer.'
                : 'Le serveur est inaccessible. Vérifiez votre connexion et réessayez.';
            setLoginStatus({ loading: false, success: false, message });
        } finally {
            window.clearTimeout(timeout);
        }
    }, [fetchUserProfileAndStoreGymId, navigate, persistTokens]);

    // Restaure la session une seule fois au démarrage. Un Ctrl+F5 n'efface
    // désormais plus les jetons stockés et ne provoque plus de déconnexion.
    useEffect(() => {
        let active = true;

        const restoreSession = async () => {
            const storedTokens = readStoredTokens();
            if (!storedTokens) {
                if (active) setLoading(false);
                return;
            }

            const decoded = decodeAccessToken(storedTokens.access);
            if (decoded && decoded.exp * 1000 > Date.now() + REFRESH_BEFORE_EXPIRY_MS) {
                setAuthTokens(storedTokens);
                setUser(decoded);
            } else {
                await refreshAccessToken(storedTokens);
            }

            if (active) setLoading(false);
        };

        restoreSession();
        return () => { active = false; };
    }, [refreshAccessToken]);

    // Vérifie périodiquement l'expiration, ainsi qu'au retour dans l'onglet.
    // Une panne réseau temporaire sera donc retentée sans supprimer la session.
    useEffect(() => {
        if (loading || !authTokens?.access || !authTokens?.refresh) return undefined;

        let refreshInProgress = false;

        const refreshIfNeeded = async () => {
            if (refreshInProgress) return;

            const decoded = decodeAccessToken(authTokens.access);
            const expiresAt = (decoded?.exp || 0) * 1000;
            if (expiresAt > Date.now() + REFRESH_BEFORE_EXPIRY_MS) return;

            refreshInProgress = true;
            try {
                await refreshAccessToken(authTokens);
            } finally {
                refreshInProgress = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                refreshIfNeeded();
            }
        };

        refreshIfNeeded();
        const interval = window.setInterval(
            refreshIfNeeded,
            REFRESH_CHECK_INTERVAL_MS,
        );

        window.addEventListener('focus', refreshIfNeeded);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', refreshIfNeeded);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [authTokens, loading, refreshAccessToken]);

    const contextData = {
        user,
        authTokens,
        loginUser,
        logoutUser,
        loginUserWithToken,
        loading,
        loginStatus: {
            ...loginStatus,
            setMessage: (message) => setLoginStatus((current) => ({ ...current, message })),
            reset: resetLoginStatus,
        },
    };

    return (
        <AuthContext.Provider value={contextData}>
            {children}
        </AuthContext.Provider>
    );
};